import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  CalendarDays,
  ClipboardCheck,
  Users,
  FileText,
  BellRing,
  PiggyBank,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Smartphone,
  Clock,
  TrendingUp,
  Mail,
} from "lucide-react";
import { api, formatCurrency } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1758685733907-42e9651721f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxwcml2YXRlJTIwdHV0b3IlMjBzdHVkZW50fGVufDB8fHx8MTc3Njg2MDE5MHww&ixlib=rb-4.1.0&q=85";
const F2 = "https://images.pexels.com/photos/5905754/pexels-photo-5905754.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Real EduFin app screenshots (captured via Playwright, served from /public/screenshots)
const SHOT_DASHBOARD = "/screenshots/dashboard.png";
const SHOT_CALENDAR = "/screenshots/calendar.png";
const SHOT_ACCOUNTING = "/screenshots/accounting.png";
const SHOT_REPORTS = "/screenshots/reports.png";
const SHOT_MOBILE_CAL = "/screenshots/mobile-calendar.png";
const SHOT_MOBILE_DASH = "/screenshots/mobile-dashboard.png";

export default function LandingPage() {
  const [price, setPrice] = useState(null);
  const [currency, setCurrency] = useState("TRY");

  useEffect(() => {
    api.get("/public/pricing").then((r) => {
      if (r.data?.annual_price != null) setPrice(r.data.annual_price);
      if (r.data?.currency) setCurrency(r.data.currency);
    }).catch(() => {
      setPrice(1200);
    });
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#1F2937]" data-testid="landing-page">
      <Navbar />
      <Hero />
      <SocialProof />
      <Features />
      <Showcase />
      <HowItWorks />
      <Pricing price={price ?? 1200} currency={currency} />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}

/* ---------------- Navbar ---------------- */
function Navbar() {
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FAF9F6]/80 border-b border-black/5">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <a href="#top" className="flex items-center gap-2 group" data-testid="nav-logo">
          <span className="w-8 h-8 rounded-md bg-[#3A5A40] text-white flex items-center justify-center group-hover:scale-105 transition-transform">
            <Sparkles size={16} strokeWidth={2} />
          </span>
          <span className="font-serif text-2xl font-semibold tracking-tight">EduFin</span>
        </a>
        <div className="hidden md:flex items-center gap-8 text-sm">
          <a href="#ozellikler" className="text-gray-600 hover:text-[#3A5A40] transition-colors" data-testid="nav-features">Özellikler</a>
          <a href="#nasil-calisir" className="text-gray-600 hover:text-[#3A5A40] transition-colors" data-testid="nav-how">Nasıl Çalışır?</a>
          <a href="#fiyatlandirma" className="text-gray-600 hover:text-[#3A5A40] transition-colors" data-testid="nav-pricing">Fiyatlandırma</a>
          <a href="#sss" className="text-gray-600 hover:text-[#3A5A40] transition-colors" data-testid="nav-faq">S.S.S.</a>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/giris"
            className="hidden sm:inline-flex text-sm px-4 py-2 text-[#3A5A40] hover:text-[#2C4631] font-medium"
            data-testid="nav-login"
          >
            Giriş Yap
          </Link>
          <Link
            to="/kayit"
            className="inline-flex items-center gap-1 rounded-full bg-[#3A5A40] hover:bg-[#2C4631] text-white px-4 sm:px-5 py-2 text-sm font-medium transition-all hover:-translate-y-0.5 shadow-sm hover:shadow-md"
            data-testid="nav-cta"
          >
            <span className="hidden sm:inline">14 gün ücretsiz dene</span>
            <span className="sm:hidden">Ücretsiz dene</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      </nav>
    </header>
  );
}

/* ---------------- Hero ---------------- */
function Hero() {
  return (
    <section id="top" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-28">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="space-y-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#3A5A40]/10 text-[#3A5A40] px-3 py-1 text-xs font-medium">
            <Sparkles size={12} /> Özel ders öğretmenleri için
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight font-semibold">
            Özel derslerinizi <span className="italic text-[#3A5A40]">sade, profesyonel</span> bir şekilde yönetin.
          </h1>
          <p className="text-lg text-gray-700 leading-relaxed max-w-xl">
            Takvim, öğrenci, muhasebe, aylık veli raporları — hepsi tek bir panelde. EduFin ile defter tutmayı bırakın, öğretmeye odaklanın.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to="/kayit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3A5A40] hover:bg-[#2C4631] text-white px-8 py-4 font-medium transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
              data-testid="hero-cta-primary"
            >
              14 gün ücretsiz dene <ArrowRight size={16} />
            </Link>
            <Link
              to="/giris"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#3A5A40] text-[#3A5A40] hover:bg-white px-8 py-4 font-medium transition-all"
              data-testid="hero-cta-secondary"
            >
              Demo hesabı incele
            </Link>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#3A5A40]" /> Kredi kartı gerekmez</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#3A5A40]" /> İstediğin zaman iptal et</span>
            <span className="inline-flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#3A5A40]" /> Türkçe destek</span>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-8 bg-gradient-to-br from-[#3A5A40]/20 via-[#D97757]/10 to-transparent blur-3xl rounded-full pointer-events-none" />
          <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 aspect-[4/5] lg:aspect-[4/5]">
            <img src={HERO_IMG} alt="Öğretmen ve öğrenci" className="w-full h-full object-cover" loading="eager" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4 ring-1 ring-black/5 max-w-[240px] hidden sm:block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#3A5A40]/10 text-[#3A5A40] flex items-center justify-center"><Clock size={18} /></div>
              <div>
                <div className="text-xs text-gray-500">Bu ay kazanç</div>
                <div className="font-serif text-xl font-semibold">₺9.300</div>
              </div>
            </div>
          </div>
          <div className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl p-4 ring-1 ring-black/5 hidden sm:block">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#D97757]/10 text-[#D97757] flex items-center justify-center"><BellRing size={18} /></div>
              <div>
                <div className="text-xs text-gray-500">Velilere otomatik</div>
                <div className="font-medium text-sm">Hatırlatma gönderildi</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Social Proof ---------------- */
function SocialProof() {
  return (
    <section className="bg-white border-y border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
        {[
          { n: "50+", l: "Öğretmen kullanıyor" },
          { n: "1.000+", l: "Ders planlandı" },
          { n: "₺200K+", l: "Takip edilen kazanç" },
          { n: "7/24", l: "Otomatik hatırlatma" },
        ].map((s) => (
          <div key={s.l} className="text-center">
            <div className="font-serif text-3xl sm:text-4xl font-semibold text-[#3A5A40]">{s.n}</div>
            <div className="text-sm text-gray-600 mt-1">{s.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Features (Bento) ---------------- */
function Features() {
  return (
    <section id="ozellikler" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
      <div className="max-w-2xl mb-14">
        <div className="text-xs uppercase tracking-wider text-[#D97757] font-semibold mb-3">Özellikler</div>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
          Defter, Excel, birbirinden ayrı uygulamalar —
          <span className="italic text-[#3A5A40]"> hepsine son.</span>
        </h2>
        <p className="text-gray-700 text-lg mt-4">
          Özel ders vermenin tüm ayrıntılarını tek bir panelde yönetin. Kurulumu 5 dakika, alışkanlığa dönüşmesi bir ders sonrası.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <BigCard
          className="md:col-span-8 md:row-span-2"
          icon={CalendarDays}
          title="52 haftalık otomatik ders takvimi"
          desc="Öğrencinin haftalık programını bir kez girin — EduFin bir yıllık tüm derslerinizi oluştursun. Çakışma uyarıları, gece yarısı sarmalı esnek saatler, tatilde otomatik atlama."
          img={SHOT_CALENDAR}
          testid="feature-calendar"
        />
        <Card
          className="md:col-span-4"
          icon={PiggyBank}
          title="Gelir & Gider Takibi"
          desc="Aylık kazanç grafiği, alacak bakiyesi, gider kategorileri. Finansal bilgileri tek dokunuşla gizle."
          accent
          testid="feature-finance"
        />
        <Card
          className="md:col-span-4"
          icon={Users}
          title="Öğrenci & Veli CRM"
          desc="İletişim bilgileri, ders ücreti, paket ders geri sayımı, kalan bakiye — hepsi tek yerde."
          testid="feature-students"
        />
        <Card
          className="md:col-span-6"
          icon={FileText}
          title="Türkçe PDF Raporlar"
          desc="Ay sonunda veliye gidecek ders ve ödeme çizelgesini tek tıkla üretin, doğru Türkçe karakterle. İsterseniz otomatik e-postayla gönderilsin."
          testid="feature-pdf"
        />
        <Card
          className="md:col-span-6"
          icon={BellRing}
          title="E-posta Hatırlatmaları"
          desc="Ders başlamadan 1 saat önce öğretmen ve veliye otomatik bildirim. Unutkanlık bitti."
          accent
          testid="feature-reminders"
        />
      </div>
    </section>
  );
}

function Card({ className, icon: Icon, title, desc, accent, testid }) {
  return (
    <div
      className={`group bg-white rounded-2xl border border-black/5 shadow-sm hover:shadow-md transition-all p-8 ${className}`}
      data-testid={testid}
    >
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-5 ${accent ? "bg-[#D97757]/10 text-[#D97757]" : "bg-[#3A5A40]/10 text-[#3A5A40]"} group-hover:scale-110 transition-transform`}>
        <Icon size={20} strokeWidth={1.75} />
      </div>
      <h3 className="font-serif text-xl sm:text-2xl font-semibold leading-tight">{title}</h3>
      <p className="text-gray-600 text-[15px] mt-2 leading-relaxed">{desc}</p>
    </div>
  );
}

function BigCard({ className, icon: Icon, title, desc, img, testid }) {
  return (
    <div
      className={`group relative bg-white rounded-2xl border border-black/5 shadow-sm hover:shadow-md transition-all overflow-hidden ${className}`}
      data-testid={testid}
    >
      <div className="p-8 md:p-10">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5 bg-[#3A5A40]/10 text-[#3A5A40] group-hover:scale-110 transition-transform">
          <Icon size={20} strokeWidth={1.75} />
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl font-semibold leading-tight max-w-md">{title}</h3>
        <p className="text-gray-600 text-[15px] mt-3 leading-relaxed max-w-lg">{desc}</p>
      </div>
      <div className="relative h-48 md:h-64 lg:h-72 overflow-hidden">
        <img src={img} alt="" className="w-full h-full object-cover opacity-95 group-hover:scale-105 transition-transform duration-700" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-t from-white/40 to-transparent" />
      </div>
    </div>
  );
}

/* ---------------- Showcase (Real Screenshots) ---------------- */
function Showcase() {
  const [active, setActive] = useState("dashboard");
  const tabs = [
    { id: "dashboard",  label: "Panel",       img: SHOT_DASHBOARD,  caption: "Tek bakışta bu ayın kazancı, alacaklar ve bekleyen onaylar." },
    { id: "calendar",   label: "Takvim",      img: SHOT_CALENDAR,   caption: "Haftalık grid, çakışma uyarıları, esnek saatler, tatilde atlama." },
    { id: "accounting", label: "Muhasebe",    img: SHOT_ACCOUNTING, caption: "12 aylık eğilim, gelir/gider kartları, Excel'e aktarım." },
    { id: "reports",    label: "Raporlar",    img: SHOT_REPORTS,    caption: "Tek tıkla veliye PDF: konu, saat, tutar, bakiye — Türkçe." },
  ];
  const current = tabs.find((t) => t.id === active) || tabs[0];

  return (
    <section className="bg-white border-y border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <div className="text-xs uppercase tracking-wider text-[#D97757] font-semibold mb-3">Gerçek Görüntüler</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
            Bir bakışta <span className="italic text-[#3A5A40]">EduFin.</span>
          </h2>
          <p className="text-gray-700 text-lg mt-4">
            Stok görseller değil — demo hesabımızdan gerçek ekran görüntüleri. İlk derste tanışacağın panel.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex flex-wrap gap-2 justify-center mb-8">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                active === t.id
                  ? "bg-[#3A5A40] text-white shadow-md"
                  : "bg-[#FAF9F6] text-gray-700 hover:bg-[#EFEEEA]"
              }`}
              data-testid={`showcase-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* MacBook frame */}
        <div className="relative max-w-5xl mx-auto">
          <div className="relative rounded-t-[20px] bg-gradient-to-b from-[#3a3a3a] to-[#1f1f1f] p-3 shadow-2xl">
            {/* Traffic lights */}
            <div className="absolute top-3 left-4 flex gap-1.5 z-10">
              <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
              <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
            </div>
            <div className="overflow-hidden rounded-[10px] bg-white aspect-[16/10]">
              <img
                key={current.img}
                src={current.img}
                alt={current.label}
                className="w-full h-full object-cover object-top animate-fade-in"
                loading="lazy"
                data-testid="showcase-image"
              />
            </div>
          </div>
          {/* Laptop base/hinge */}
          <div className="h-3 bg-gradient-to-b from-[#cbcbcb] via-[#a8a8a8] to-[#8a8a8a] rounded-b-[6px] mx-[-2%]" />
          <div className="h-2 bg-gradient-to-b from-[#8a8a8a] to-transparent rounded-b-full mx-[10%]" />
          <p className="text-center text-sm text-gray-600 mt-6 italic">{current.caption}</p>
        </div>

        {/* Mobile phones showcase */}
        <div className="mt-20 grid md:grid-cols-2 gap-10 md:gap-16 items-center max-w-4xl mx-auto">
          <div className="order-2 md:order-1 space-y-4 text-center md:text-left">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#D97757]/10 text-[#D97757] px-3 py-1 text-xs font-semibold">
              <Smartphone size={12} /> Mobilde uygulama gibi
            </div>
            <h3 className="font-serif text-2xl sm:text-3xl font-semibold leading-tight">
              Telefonuna kur — bir dokunuşla eriş.
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Mini aylık takvim, kart düzenli muhasebe, anında gün detayı. Kendi ikonunla ana ekranda — uygulama mağazası istemez.
            </p>
            <ul className="space-y-2 text-sm text-gray-700 inline-block text-left">
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#3A5A40] flex-shrink-0 mt-0.5" /> Çevrimdışı mod ile ders detayları</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#3A5A40] flex-shrink-0 mt-0.5" /> iOS ve Android (PWA)</li>
              <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#3A5A40] flex-shrink-0 mt-0.5" /> Hızlı başlangıç rehberi</li>
            </ul>
          </div>
          <div className="order-1 md:order-2 relative flex items-center justify-center gap-4 md:gap-6">
            <PhoneFrame img={SHOT_MOBILE_CAL} alt="Mobil takvim" className="transform -rotate-6 -translate-y-4" delay={0} />
            <PhoneFrame img={SHOT_MOBILE_DASH} alt="Mobil panel" className="transform rotate-3 z-10 shadow-2xl" delay={100} />
          </div>
        </div>
      </div>
    </section>
  );
}

function PhoneFrame({ img, alt, className = "", delay = 0 }) {
  return (
    <div
      className={`relative w-[180px] sm:w-[210px] rounded-[28px] bg-gradient-to-b from-[#2b2b2b] to-[#111] p-[6px] shadow-xl transition-transform hover:scale-105 hover:rotate-0 ${className}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-14 h-3.5 bg-black rounded-b-xl z-10" />
      <div className="overflow-hidden rounded-[22px] bg-white aspect-[390/844]">
        <img src={img} alt={alt} loading="lazy" className="w-full h-full object-cover object-top" />
      </div>
    </div>
  );
}


/* ---------------- How It Works ---------------- */
function HowItWorks() {
  const steps = [
    { n: "01", title: "Ücretsiz deneme ile başlayın", desc: "Kredi kartı istemiyoruz. 14 gün boyunca tüm özellikler kilidiz. Yıllık aboneliğe karar vermek için bolca vakit.", icon: Sparkles },
    { n: "02", title: "Öğrencilerinizi ekleyin", desc: "Veli bilgileri, ders ücreti, haftalık saatler — 2 dakikada tanımlayın. EduFin 52 haftalık takvimi sizin için oluştursun.", icon: Users },
    { n: "03", title: "Panelden tek yerden yönetin", desc: "Dersi onaylayın, ödemeyi girin, ay sonunda veliye PDF gönderin. Sabah-akşam 4 farklı not defterine gerek kalmadan.", icon: TrendingUp },
  ];
  return (
    <section id="nasil-calisir" className="bg-white border-y border-black/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-wider text-[#D97757] font-semibold mb-3">Nasıl Çalışır?</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
            İlk dersinize 5 dakika içinde hazır olun.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-8 md:gap-12">
          {steps.map((s) => (
            <div key={s.n} className="space-y-4" data-testid={`how-step-${s.n}`}>
              <div className="flex items-center gap-3">
                <div className="font-serif text-4xl text-[#3A5A40] font-semibold">{s.n}</div>
                <div className="w-10 h-10 rounded-full border border-[#3A5A40]/30 flex items-center justify-center text-[#3A5A40]">
                  <s.icon size={18} strokeWidth={1.75} />
                </div>
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-semibold leading-tight">{s.title}</h3>
              <p className="text-gray-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Pricing ---------------- */
function Pricing({ price, currency }) {
  const features = [
    "Sınırsız öğrenci ve ders",
    "52 haftalık otomatik takvim",
    "Çakışma ve tatil kontrolü",
    "Türkçe PDF raporlar",
    "Otomatik e-posta hatırlatmaları",
    "Muhasebe ve grafik panelleri",
    "Veri dışa aktarma (JSON + CSV)",
    "Mobilde uygulama gibi çalışır (PWA)",
    "Türkçe destek",
  ];
  return (
    <section id="fiyatlandirma" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
      <div className="text-center max-w-2xl mx-auto mb-14">
        <div className="text-xs uppercase tracking-wider text-[#D97757] font-semibold mb-3">Fiyatlandırma</div>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
          Basit. Tek plan. <span className="italic text-[#3A5A40]">Yıllık.</span>
        </h2>
        <p className="text-gray-700 text-lg mt-4">
          Aylık abonelik karmaşası yok. Yılda bir kere ödeyin, 12 ay boyunca her şey aktif.
        </p>
      </div>

      <div className="relative bg-white border-2 border-[#3A5A40] rounded-3xl p-8 md:p-10 max-w-lg mx-auto shadow-xl" data-testid="pricing-card">
        <div className="absolute top-4 right-4 bg-[#D97757] text-white px-3 py-1 rounded-full text-xs font-bold tracking-wide">
          14 GÜN ÜCRETSİZ
        </div>
        <div className="text-sm uppercase tracking-wider text-[#3A5A40] font-semibold mb-2">EduFin Yıllık</div>
        <div className="flex items-baseline gap-2 mb-1">
          <span className="font-serif text-5xl sm:text-6xl font-semibold text-[#1F2937]" data-testid="pricing-amount">
            {formatCurrency(price, currency)}
          </span>
          <span className="text-gray-500">/ yıl</span>
        </div>
        <div className="text-sm text-gray-500 mb-8">~{formatCurrency(Math.round(price / 12), currency)} / ay · Havale ile ödeme</div>

        <ul className="space-y-3 mb-8">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 text-[15px] text-gray-700">
              <CheckCircle2 size={18} className="text-[#3A5A40] flex-shrink-0 mt-0.5" strokeWidth={2} />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        <Link
          to="/kayit"
          className="flex items-center justify-center gap-2 w-full rounded-full bg-[#3A5A40] hover:bg-[#2C4631] text-white px-8 py-4 font-medium transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg"
          data-testid="pricing-cta"
        >
          14 gün ücretsiz dene <ArrowRight size={16} />
        </Link>
        <div className="text-xs text-center text-gray-500 mt-4">
          Kredi kartı gerekmez · İstediğin zaman iptal et · Taahhüt yok
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */
function FAQ() {
  const faqs = [
    {
      q: "Ücretsiz deneme gerçekten ücretsiz mi?",
      a: "Evet. 14 gün boyunca tüm özelliklere erişirsiniz. Kredi kartı bilgisi istemiyoruz, taahhüt yok. Deneme bittiğinde siz karar verene kadar ödeme istenmez.",
    },
    {
      q: "Kaç öğrenci ekleyebilirim?",
      a: "Sınırsız. Yıllık abonelikte öğrenci, ders, rapor veya veri depolama limiti yok.",
    },
    {
      q: "Verilerim güvende mi?",
      a: "Tüm veriler şifreli bağlantı (HTTPS) ile saklanır. Şifreniz bcrypt ile hash'lenir — biz bile göremeyiz. Ayarlar'dan verilerinizi istediğiniz zaman JSON veya Excel (CSV) olarak dışa aktarabilirsiniz.",
    },
    {
      q: "Ödeme nasıl alınır?",
      a: "Şu an yalnızca banka havalesi/EFT ile ödeme alınmaktadır. Hesap bilgilerimizi deneme süreniz bittiğinde size bildiririz. Ödeme onaylanınca hesabınız otomatik olarak 12 ay aktif edilir.",
    },
    {
      q: "Veliye PDF rapor Türkçe karakterle gönderilir mi?",
      a: "Evet. Ş, Ğ, İ, Ö, Ç, Ü gibi tüm Türkçe karakterler PDF'te doğru görünür. Ay sonunda otomatik gönderim aktif olarak çalışır, isterseniz manuel olarak da tek tıkla gönderebilirsiniz.",
    },
    {
      q: "Telefondan da kullanabilir miyim?",
      a: "Elbette. EduFin PWA olarak tasarlanmıştır — telefonunuza 'Ana Ekrana Ekle' diyerek uygulama gibi açabilirsiniz. Mobil takvim, mini aylık görünüm ve kart düzenli muhasebe ile her yerde yönetin.",
    },
    {
      q: "İptal edebilir miyim?",
      a: "Tabii ki. İstediğiniz zaman hesabınızdan ayrılabilirsiniz. Abonelik süreniz bitene kadar tüm özellikler açık kalır, sonra hesabınız pasif olur ve verileriniz 30 gün daha saklanır.",
    },
  ];
  return (
    <section id="sss" className="bg-white border-y border-black/5">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-wider text-[#D97757] font-semibold mb-3">Sıkça Sorulan Sorular</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
            Aklınızdaki soruların yanıtı.
          </h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
              <AccordionTrigger className="font-serif text-lg sm:text-xl text-left hover:no-underline">{f.q}</AccordionTrigger>
              <AccordionContent className="text-gray-700 text-[15px] leading-relaxed">
                {f.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ---------------- Final CTA ---------------- */
function FinalCTA() {
  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
      <div className="relative overflow-hidden rounded-3xl bg-[#3A5A40] p-10 md:p-16 text-white">
        <div className="absolute inset-0 opacity-20">
          <img src={F2} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#3A5A40] via-[#3A5A40]/90 to-transparent" />
        <div className="relative max-w-2xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium mb-6">
            <ShieldCheck size={12} /> Türk öğretmenler için tasarlandı
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold leading-tight">
            Defter tutmayı bırakın.
            <br />
            <span className="italic">Öğretmeye odaklanın.</span>
          </h2>
          <p className="text-white/85 text-lg mt-4 max-w-xl">
            Bugün ücretsiz deneme ile başlayın. Kurulum 5 dakika, ilk ders onayı 30 saniye sürer.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link
              to="/kayit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-[#3A5A40] hover:bg-[#FAF9F6] px-8 py-4 font-medium transition-all hover:-translate-y-0.5 shadow-lg"
              data-testid="final-cta-primary"
            >
              14 gün ücretsiz dene <ArrowRight size={16} />
            </Link>
            <Link
              to="/giris"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/40 text-white hover:bg-white/10 px-8 py-4 font-medium transition-all"
              data-testid="final-cta-secondary"
            >
              Giriş yap
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Footer ---------------- */
function Footer() {
  return (
    <footer className="bg-white border-t border-black/5" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-4 gap-8">
        <div className="md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-8 h-8 rounded-md bg-[#3A5A40] text-white flex items-center justify-center"><Sparkles size={16} /></span>
            <span className="font-serif text-2xl font-semibold">EduFin</span>
          </div>
          <p className="text-gray-600 max-w-sm">
            Özel ders veren öğretmenlerin iş yükünü azaltır. Takvim, öğrenci, muhasebe — hepsi tek panelde.
          </p>
        </div>
        <div>
          <div className="text-sm font-semibold mb-3">Keşfet</div>
          <ul className="space-y-2 text-sm text-gray-600">
            <li><a href="#ozellikler" className="hover:text-[#3A5A40]">Özellikler</a></li>
            <li><a href="#nasil-calisir" className="hover:text-[#3A5A40]">Nasıl Çalışır?</a></li>
            <li><a href="#fiyatlandirma" className="hover:text-[#3A5A40]">Fiyatlandırma</a></li>
            <li><a href="#sss" className="hover:text-[#3A5A40]">S.S.S.</a></li>
          </ul>
        </div>
        <div>
          <div className="text-sm font-semibold mb-3">Hesap</div>
          <ul className="space-y-2 text-sm text-gray-600">
            <li><Link to="/giris" className="hover:text-[#3A5A40]">Giriş Yap</Link></li>
            <li><Link to="/kayit" className="hover:text-[#3A5A40]">Hesap Oluştur</Link></li>
            <li><a href="mailto:destek@edufin.com" className="hover:text-[#3A5A40] inline-flex items-center gap-1"><Mail size={12} /> destek@edufin.com</a></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-black/5 py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
        <div>© {new Date().getFullYear()} EduFin · Öğretmenler için.</div>
        <div className="inline-flex items-center gap-1.5"><Smartphone size={12} /> Telefona yüklenebilir (PWA)</div>
      </div>
    </footer>
  );
}
